// Here lies the dark magic that makes the MDX files work as routes in the app
// This is a careful arrangement of vite extensions, react-router, and MDX
// But with this in place, adding a .mdx page in this docs directory will
// create a new route.
// The sidebar navigation still needs to be manually managed. Edit nav-docs.tsx
import React, { lazy } from "react"

import { InfoCard, type InfoCardProps } from "@/components/docs/info-card"
import { Example, type ExampleProps } from "@/components/example"
import { Button } from "@/components/ui/button"

// this glob imports all .mdx files in the current directory and subdirectories
// vite will manage this & pre-execute this at build time
const mdxModules = import.meta.glob("./**/*.mdx")

export type DocsPageItem = { path: string; title: string }

// all components here are passed to .mdx files, if you want to add components
// default available to all MDX, do so here
const mdxComponents = {
  h5: (props: React.ComponentPropsWithoutRef<"h5">) => (
    <p
      className="mt-8 text-pretty text-lg font-medium text-gray-500 sm:text-xl/8"
      {...props}
    />
  ),
  Info: (props: InfoCardProps) => <InfoCard {...props} />,
  Button: (props: React.ComponentPropsWithoutRef<"button">) => (
    <Button {...props} />
  ),
  Example: (props: ExampleProps) => <Example example={props.example} />,
}

function wrappedMdxComponent(Component: React.ComponentType) {
  return function WrappedComponent(props: any) {
    // TODO - this is likely the right place to do session loading & user fetching
    // tricks
    return <Component components={mdxComponents} {...props} />
  }
}

// this unpacks the raw .mdx file and returns a lazy loaded component, wrapped
// with the props that we want to pass to the component
function docsPageLoader(mod: () => Promise<{ default: React.ComponentType }>) {
  return lazy(async () => {
    const module = await mod().then((mod) => ({
      default: wrappedMdxComponent(mod.default),
    }))
    return { default: module.default }
  })
}

// This is the thing that goes to the router. It maps the .mdx files to
// react-router routes
export const docsRoutes = Object.keys(mdxModules).map((path) => {
  const mod = mdxModules[path]
  const Component = docsPageLoader(
    mod as () => Promise<{ default: React.ComponentType }>,
  )
  return {
    path: path
      .replace(/\.mdx$/, "")
      .replace(/.\//, "")
      .replace("page", ""),
    element: <Component />,
  }
})
